import {
    Column,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { ReactElement, CSSProperties } from "react";

export type TableProps<T> = {
    data: T[];
    columns: ColumnDef<T, any>[];
    loading?: boolean | null;
    loadError?: boolean | null;
    tableSize?: "xs" | "sm" | "md" | "lg";
    icon: ReactElement;
    errorTitle: string;
    errorText: string;
    emptyTitle: string;
    emptyText: string;
    emptyAction: ReactElement | null;
};

export function SimpleTable<T>({
    data,
    columns,
    loading,
    loadError,
    tableSize,
    icon,
    errorTitle,
    errorText,
    emptyTitle,
    emptyText,
    emptyAction,
}: TableProps<T>) {
    const table = useReactTable({
        data,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const getCommonStyle = (column: Column<T>): CSSProperties => {
        return {
            width: column.getSize(),
        };
    };

    return (
        <div className="card card-compact w-full h-full flex flex-col bg-gray-50 overflow-y-auto">
            <div
                className={`card-body py-0! px-0! gap-0! block! overflow-hidden h-full`}
            >
                <div className="h-full flex flex-col">
                    <div className="flex-1 overflow-auto">
                        {loading ? (
                            <div className="w-full h-full flex justify-center items-center flex-row">
                                <span className="loading loading-spinner loading-lg text-gray-600"></span>
                            </div>
                        ) : loadError || data.length == 0 ? (
                            <div className="w-full h-full flex justify-center items-center flex-col text-center">
                                <div className="flex flex-col items-center mb-8 w-lg">
                                    <div className="bg-gray-100 p-5 rounded-full mb-4">
                                        {icon}
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-700 mb-2">
                                        {loadError ? errorTitle : emptyTitle}
                                    </h2>
                                    <p className="text-gray-600">
                                        {loadError ? errorText : emptyText}
                                    </p>
                                </div>

                                {/* Hide empty action on load error */}
                                {loadError ? "" : emptyAction}
                            </div>
                        ) : (
                            <table
                                className={clsx(
                                    "w-full table table-fixed border-collapse border-spacing-0 table-pin-rows",
                                    {
                                        "table-xs": tableSize === "xs",
                                        "table-sm": tableSize === "sm",
                                        "table-md": tableSize === "md",
                                        "table-lg": tableSize === "lg",
                                    },
                                )}
                            >
                                <thead>
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => {
                                            return (
                                                <tr
                                                    key={headerGroup.id}
                                                    className="z-2"
                                                >
                                                    {headerGroup.headers.map(
                                                        (header) => (
                                                            <th
                                                                key={header.id}
                                                                colSpan={
                                                                    header.colSpan
                                                                }
                                                                className={clsx(
                                                                    "align-bottom",
                                                                    {
                                                                        xs: "text-xs",
                                                                        sm: "text-sm",
                                                                        md: "text-base",
                                                                        lg: "text-lg",
                                                                        default:
                                                                            "text-base",
                                                                    }[
                                                                        tableSize ??
                                                                            "default"
                                                                    ],
                                                                )}
                                                                style={{
                                                                    ...getCommonStyle(
                                                                        header.column,
                                                                    ),
                                                                }}
                                                            >
                                                                <div className="flex items-center">
                                                                    {flexRender(
                                                                        header
                                                                            .column
                                                                            .columnDef
                                                                            .header,
                                                                        header.getContext(),
                                                                    )}
                                                                </div>
                                                            </th>
                                                        ),
                                                    )}
                                                </tr>
                                            );
                                        })}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row) => {
                                        return (
                                            <tr key={row.id}>
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell) => (
                                                        <td
                                                            key={cell.id}
                                                            className={clsx({
                                                                "bg-gray-200/70!":
                                                                    row.getIsSelected(),
                                                            })}
                                                        >
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext(),
                                                            )}
                                                        </td>
                                                    ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
